import type {
  QueryMetadataResponse,
  QueryMetadataResponseV3_5,
  KeywordsDomain,
  SearchMetadata,
  SearchParams,
  ExpandSearchParams,
  SearchResult,
  SearchMetadataResponse,
  CriteriaV3_5,
} from "$lib/types";

export class SearchAPI {
  private static readonly BASE_URL = "/api/v1/search";
  private static readonly ENDPOINTS = {
    start: (version?: string) => (version === "v3.5" ? "/start_search_v_3_5" : "/start_search"),
    expand: (version?: string) => (version === "v3.5" ? "/start_expand_search_v_3_5" : "/start_expand_search"),
    queryMetadata: (version?: string) => (version === "v3.5" ? "/metadata_v_3_5" : "/metadata"),
    subscribe: "/subscribe_search",
    expandSubscribe: "/subscribe_expand_search",
    searchResultsByID: "/get_paginated_candidates_with_search_id",
    searchResultsByIDAuth: "/get_paginated_candidates_with_search_id_auth",
    searchResultsBySlug: "/get_paginated_candidates_with_search_slug",
    searchResultsBySlugAuth: "/get_paginated_candidates_with_search_slug_auth",
    pastSearchesForUser: "/get_paginated_searches_with_user",
    deleteSearchByID: "/remove_search_with_search_id",
    deleteSearchBySlug: "/remove_search_with_search_slug",
    searchMetadataByID: "/get_search_metadata",
    searchMetadataByIDAuth: "/get_search_metadata_auth",
    searchMetadataBySlug: "/get_search_metadata_slug",
    searchMetadataBySlugAuth: "/get_search_metadata_slug_auth",
    csvDownload: "/csv_download",
    csvDownloadWithEmail: "/csv_download_email",
    checkCSVStatus: "/check_csv_status",
    updatePublicStatus: "/update_search_public_status",
  } as const;

  private static readonly HEADERS = {
    contentType: "Content-Type",
    jsonContentType: "application/json",
  } as const;

  private static readonly DEFAULT_MODEL = {
    value: "gpt-4o-mini",
    label: "gpt-4o-mini",
  } as const;

  /**
   * Transforms keywords to the format expected by the API
   * @private
   */
  private static transformKeywords(
    keywords: string[] | KeywordsDomain[],
    version?: string,
  ): string[] | KeywordsDomain[] {
    if (!keywords || keywords.length === 0) return [];

    // If it's v3.5, return as is if it's already in KeywordsDomain format
    if (version === "v3.5") {
      if (typeof keywords[0] === "object" && "keywords" in keywords[0]) {
        return keywords as KeywordsDomain[];
      }
      // Convert string[] to KeywordsDomain[] if needed
      return (keywords as string[]).map((k) => ({
        keywords: k,
        domain: "default",
      }));
    }

    // For v3.0 or unspecified version, ensure we return string[]
    if (typeof keywords[0] === "object" && "keywords" in keywords[0]) {
      return (keywords as KeywordsDomain[]).map((k) => k.keywords);
    }
    return keywords as string[];
  }

  /**
   * Saves a new search and returns search ID and slug
   * @throws Error if the fetch request fails
   */
  static async start(params: SearchParams & { version?: string }): Promise<{ search_id: string; search_slug: string }> {
    try {
      // const transformedKeywords = this.transformKeywords(params.keywords, params.version);

      // Create base request body
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const requestBody: any = {
        query: params.query,
        // keywords: transformedKeywords,
        // category: params.category,
        page_num: params.pageNum || 4,
        definite_match_count: params.definiteMatchCount || 0,
        mode: params.mode,
      };

      // Handle criteria for v3.5
      if (params.version === "v3.5") {
        requestBody.search_order = params.searchOrder;
        requestBody.criteria = params.criteria;
      } else {
        requestBody.criteria = params.criteria;
      }

      logger.log("Making search request:", {
        version: params.version,
        url: `${this.BASE_URL}${this.ENDPOINTS.start(params.version)}`,
        body: requestBody,
      });

      const response = await fetch(`${this.BASE_URL}${this.ENDPOINTS.start(params.version)}`, {
        method: "POST",
        headers: {
          [this.HEADERS.contentType]: this.HEADERS.jsonContentType,
        },
        body: JSON.stringify(requestBody),
        credentials: "include",
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Search error response:", errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Save search error:", error);
      throw new Error(`Failed to save search: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }

  static async fetchQueryMetadata(params: {
    query: string;
    modelValue: string;
    _auth?: boolean;
    version?: string;
  }): Promise<QueryMetadataResponse | QueryMetadataResponseV3_5> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 12000);

    try {
      const response = await fetch(`${this.BASE_URL}${this.ENDPOINTS.queryMetadata(params.version)}`, {
        method: "POST",
        headers: {
          [this.HEADERS.contentType]: this.HEADERS.jsonContentType,
        },
        body: JSON.stringify({ query: params.query, model: params.modelValue }),
        credentials: "include",
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        if (response.status === 402) {
          throw new Error("Payment required. Please upgrade your plan.");
        } else if (response.status === 403) {
          throw new Error("You do not have permission to perform this action. Please upgrade your plan.");
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      }

      const data = await response.json();
      logger.log("Query metadata pre-validation:", data);
      if (params.version && params.version.toString() === "v3.5") {
        logger.log("validating as v3.5");
        return this.validateAndNormalizeQueryMetadataV3_5(data);
      } else {
        logger.log("validating as v3");
        return this.validateAndNormalizeQueryMetadata(data);
      }
    } catch (error) {
      clearTimeout(timeoutId);

      console.error("Raw metadata fetch error:", error);
      window.location.reload();

      if (error instanceof Error) {
        if (error.name === "AbortError") {
          throw new Error("Request timed out after 5 seconds");
        }
        throw new Error(`Failed to fetch metadata: ${error.message}`);
      }

      throw new Error("Failed to fetch metadata: Unknown error");
    }
  }

  // Add these new methods to the SearchAPI class:
  /**
   * Starts a search expansion and returns the expansion ID
   * @throws Error if the fetch request fails
   */
  static async startExpand(params: {
    searchId: string;
    definiteMatchCount: string;
    version?: string;
  }): Promise<{ expansion_id: string }> {
    try {
      const response = await fetch(`${this.BASE_URL}${this.ENDPOINTS.expand(params.version)}`, {
        method: "POST",
        headers: {
          [this.HEADERS.contentType]: this.HEADERS.jsonContentType,
        },
        body: JSON.stringify({
          search_id: params.searchId,
          max_definite_match_count: params.definiteMatchCount,
        }),
        credentials: "include",
      });

      logger.log("Start expansion response:", response);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Start expansion error:", error);
      throw new Error(`Failed to start expansion: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }

  /**
   * Fetches paginated search results using a search slug
   * @throws Error if the fetch request fails
   */
  static async getSearchResultsBySlug(
    searchSlug: string,
    limit: number,
    offset: number,
    auth?: boolean,
    userID?: string,
    publicFallback?: boolean,
    definiteOnly?: boolean,
  ): Promise<SearchResult[]> {
    logger.log("getSearchResultsBySlug", searchSlug, "limit", limit, "offset", offset);
    const endpoint = auth ? this.ENDPOINTS.searchResultsBySlugAuth : this.ENDPOINTS.searchResultsBySlug;
    try {
      const response = await fetch(`${this.BASE_URL}${endpoint}`, {
        method: "POST",
        headers: {
          [this.HEADERS.contentType]: this.HEADERS.jsonContentType,
        },
        body: JSON.stringify({
          searchSlug,
          limit,
          offset,
          userID: auth ? Number(userID) || 0 : undefined,
          // definiteOnly: definiteOnly ?? false,
        }),
        credentials: "include",
      });

      if (!response.ok) {
        if (publicFallback) {
          return await SearchAPI.getSearchResultsBySlug(searchSlug, limit, offset, !auth, userID);
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      logger.log("got search results by slug:", data);
      return data;
    } catch (error) {
      console.error("Search results by slug fetch error:", error);
      throw new Error(`Failed to fetch search results: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }

  /**
   * Fetches paginated search results using a search id
   * @throws Error if the fetch request fails
   */
  static async getSearchResultsByID(
    searchID: string,
    limit: number,
    offset: number,
    auth?: boolean,
    userID?: string,
  ): Promise<SearchResult[]> {
    logger.log("getSearchResultsByID", searchID, limit, offset);
    const endpoint = auth ? this.ENDPOINTS.searchResultsByIDAuth : this.ENDPOINTS.searchResultsByID;
    try {
      const response = await fetch(`${this.BASE_URL}${endpoint}`, {
        method: "POST",
        headers: {
          [this.HEADERS.contentType]: this.HEADERS.jsonContentType,
        },
        body: JSON.stringify({ searchID, limit, offset, userID: userID ? Number(userID) : undefined }),
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      logger.log("got search results by slug:", data);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error("Search results by slug fetch error:", error);
      throw new Error(`Failed to fetch search results: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }

  /**
   * Removes a search using a search ID
   * @throws Error if the fetch request fails
   */
  static async removeSearchById(searchId: string): Promise<void> {
    logger.log("removeSearchById", searchId);
    try {
      const response = await fetch(`${this.BASE_URL}${this.ENDPOINTS.deleteSearchByID}`, {
        method: "POST",
        headers: {
          [this.HEADERS.contentType]: this.HEADERS.jsonContentType,
        },
        body: JSON.stringify({ searchId }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error("Remove search by ID error:", error);
      throw new Error(`Failed to remove search: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }

  /**
   * Updates the public status of a search
   * @param searchId - The ID of the search to update
   * @param isPublic - Boolean indicating whether the search should be public
   * @throws Error if the fetch request fails
   */
  static async updateSearchPublicStatus(searchId: number | string, isPublic: boolean): Promise<void> {
    logger.log("updateSearchPublicStatus", searchId, isPublic);
    try {
      const response = await fetch(`${this.BASE_URL}${this.ENDPOINTS.updatePublicStatus}`, {
        method: "POST",
        headers: {
          [this.HEADERS.contentType]: this.HEADERS.jsonContentType,
        },
        body: JSON.stringify({
          searchId: Number(searchId),
          isPublic,
        }),
        credentials: "include",
      });

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error("You do not have permission to perform this action. Please upgrade your plan.");
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error("Failed to update search public status");
      }
    } catch (error) {
      console.error("Update search public status error:", error);
      throw new Error(
        `Failed to update search public status: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }
  }

  /**
   * Fetches search metadata using a search slug
   * @throws Error if the fetch request fails
   */
  static async getSearchMetadataBySlug(
    searchSlug: string,
    auth?: boolean,
    userID?: string,
    publicFallback?: boolean,
  ): Promise<SearchMetadata> {
    const endpoint = auth ? this.ENDPOINTS.searchMetadataBySlugAuth : this.ENDPOINTS.searchMetadataBySlug;
    try {
      logger.log("getSearchMetadataBySlug", searchSlug, auth, userID);
      const response = await fetch(`${this.BASE_URL}${endpoint}`, {
        method: "POST",
        headers: {
          [this.HEADERS.contentType]: this.HEADERS.jsonContentType,
        },
        body: JSON.stringify({
          searchSlug,
          userID: auth ? Number(userID || 0) : undefined,
        }),
        credentials: "include",
      });

      if (!response.ok) {
        if (publicFallback) {
          logger.log("public fallback");
          const fallbackData = await SearchAPI.getSearchMetadataBySlug(searchSlug, !auth, userID);
          return fallbackData;
        } else {
          logger.log("no public fallback");
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      }

      const data = await response.json();
      logger.log("got search metadata by slug:", data);
      return this.validateAndNormalizeSearchMetadata(data);
    } catch (error) {
      console.error("Search metadata by slug fetch error:", error);
      throw new Error(`Failed to fetch search metadata: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }

  /**
   * Fetches paginated searches for a given user by their userID with limit and offset
   * @throws Error if the fetch request fails
   */
  static async getPaginatedSearchesForUser(userID: number, limit: number, offset: number): Promise<SearchMetadata[]> {
    logger.log("getPaginatedSearchesForUser", userID, limit, offset);
    try {
      const response = await fetch(`${this.BASE_URL}${this.ENDPOINTS.pastSearchesForUser}`, {
        method: "POST",
        headers: {
          [this.HEADERS.contentType]: this.HEADERS.jsonContentType,
        },
        body: JSON.stringify({ userID, limit, offset }),
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      logger.log("Fetched paginated searches for user:", data);

      return data.map((item: SearchMetadataResponse) => ({
        id: this.validateNumber(item.searchID, "searchID").toString(),
        query: this.validateString(item.searchQuery, "searchQuery"),
        criteria: this.validateArray(item.criteria, "criteria"),
        category: this.validateString(item.category, "category"),
        keywords: this.validateArray(item.keywords, "keywords"),
        mode: this.validateString(item.mode, "mode"),
        userID: this.validateNumber(item.userID, "userID").toString(),
        createdAt: new Date(item.createdAt || new Date()),
        updatedAt: new Date(item.updatedAt || new Date()),
        numPages: typeof item.numPages === "number" ? item.numPages : 1,
        slug: this.validateString(item.searchSlug, "searchSlug", ""),
        status: this.validateString(item.status, "status", "visible"),
        definiteMatch: this.validateNumber(item.definiteMatch, "definiteMatch", 0),
        partialMatch: this.validateNumber(item.partialMatch, "partialMatch", 0),
        matchesCount:
          this.validateNumber(item.definiteMatch, "definiteMatch", 0) +
          this.validateNumber(item.partialMatch, "partialMatch", 0),
      }));
    } catch (error) {
      console.error("Paginated searches fetch error:", error);
      throw new Error(
        `Failed to fetch paginated searches: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }
  }

  /**
   * Removes a search using a search slug
   * @throws Error if the fetch request fails
   */
  static async removeSearchBySlug(searchSlug: string): Promise<void> {
    logger.log("removeSearchBySlug", searchSlug);
    try {
      const response = await fetch(`${this.BASE_URL}${this.ENDPOINTS.deleteSearchBySlug}`, {
        method: "POST",
        headers: {
          [this.HEADERS.contentType]: this.HEADERS.jsonContentType,
        },
        body: JSON.stringify({ searchSlug }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error("Remove search by slug error:", error);
      throw new Error(`Failed to remove search: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }

  /**
   * Validates and normalizes metadata response, providing defaults where needed
   * @private
   */
  private static validateAndNormalizeQueryMetadata(data: unknown): QueryMetadataResponse {
    try {
      if (!data || typeof data !== "object") {
        throw new Error("Metadata response must be an object");
      }

      const response = data as Record<string, unknown>;

      // Validate and normalize each field
      let criteria: string[] = [];
      try {
        criteria = this.validateArray(response.criteria, "criteria");
      } catch (_) {
        if (typeof criteria === "string") criteria = [criteria];
      }
      const keywords = this.validateArray(response.keywords, "keywords");
      const searchType = this.validateString(response.searchType, "searchType");
      const numPages = this.validateNumber(response.numPages, "numPages", 1);
      const model = this.validateModel(response.model);

      return {
        criteria,
        keywords,
        searchType,
        numPages,
        model,
      };
    } catch (error) {
      console.error("Metadata validation error:", error);
      throw new Error(`Invalid metadata response: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }

  /**
   * Validates and normalizes metadata response, providing defaults where needed
   * @private
   */
  private static validateAndNormalizeQueryMetadataV3_5(data: unknown): QueryMetadataResponseV3_5 {
    try {
      if (!data || typeof data !== "object") {
        throw new Error("Metadata response must be an object");
      }

      const response = data as Record<string, unknown>;
      logger.log("Raw metadata response:", response);

      const criteria = (response.criteria || []) as CriteriaV3_5[];
      const keywords = response.keywords as KeywordsDomain[];
      const searchType = this.validateString(response.searchType, "searchType");
      const searchOrder = this.validateString(response.searchOrder, "searchOrder", "personFirst");

      return {
        criteria,
        keywords,
        searchType,
        searchOrder,
      };
    } catch (error) {
      console.error("Metadata validation error:", error);
      throw new Error(`Invalid metadata response: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }

  /**
   * Validates and normalizes search metadata response
   * @private
   */
  private static validateAndNormalizeSearchMetadata(data: unknown): SearchMetadata {
    try {
      if (!data || typeof data !== "object") {
        throw new Error("Metadata response must be an object");
      }

      const response = data as Record<string, unknown>;

      // Validate and normalize each field
      const category = this.validateString(response.category, "category");
      const criteria = this.validateArray(response.criteria, "criteria");
      const definiteMatch = this.validateNumber(response.definiteMatch, "definiteMatch", 0);
      const keywords = this.validateArray(response.keywords, "keywords");
      const mode = this.validateString(response.mode, "mode", "default");
      const numPages = this.validateNumber(response.numPages, "numPages", 1);
      const partialMatch = this.validateNumber(response.partialMatch, "partialMatch", 0);
      const id = String(this.validateNumber(response.searchID, "searchID", 0));
      const query = this.validateString(response.searchQuery, "searchQuery");
      const slug = this.validateString(response.searchSlug, "searchSlug");
      // const totalResults = this.validateNumber(response.totalResults, "totalResults", 0);
      const updatedAt = new Date(this.validateString(response.updatedAt, "updatedAt"));
      const createdAt = new Date(this.validateString(response.createdAt, "createdAt"));
      const userID = String(response.userID);
      const matchesCount = this.validateNumber(response.matchesCount, "matchesCount");
      const searchStatus = this.validateString(response.searchStatus, "searchStatus");
      const isPublic = this.validateBoolean(response.isPublic, "isPublic", false);
      const algoVersion = this.validateString(response.algoVersion, "algoVersion");

      return {
        id,
        category,
        createdAt,
        criteria,
        definiteMatch,
        keywords,
        mode,
        numPages,
        partialMatch,
        query,
        slug,
        searchStatus,
        // totalResults,
        updatedAt,
        userID,
        matchesCount,
        isPublic,
        algoVersion,
      };
    } catch (error) {
      console.error("Metadata validation error:", error);
      throw new Error(`Invalid metadata response: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }

  static async downloadCSV(searchId: string | number, userId: string | number, searchQuery?: string): Promise<void> {
    try {
      const params = {
        search_id: searchId.toString(),
        user_id: userId.toString(),
        email_gen: "false",
      };

      const queryString = new URLSearchParams(params).toString();
      const url = `${this.BASE_URL}${this.ENDPOINTS.csvDownload}?${queryString}`;
      const response = await fetch(url, {
        method: "GET",
        credentials: "include",
      });

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error("You do not have permission to perform this action. Please upgrade your plan.");
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = downloadUrl;
      const filename = searchQuery
        ? `${searchQuery.toLowerCase().replace(/\s+/g, "-")}-${Date.now()}.csv`
        : `search-data-${searchId}.csv`;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("CSV download error:", error);
      throw new Error(`Failed to download CSV: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }

  /**
   * Starts a premium CSV download process with email addresses
   * @throws Error if the fetch request fails
   */
  static async startCSVDownloadWithEmail(searchId: string | number, userId: string | number): Promise<boolean> {
    try {
      const params = {
        search_id: searchId.toString(),
        user_id: userId.toString(),
        email_gen: "true",
      };

      const queryString = new URLSearchParams(params).toString();
      const url = `${this.BASE_URL}${this.ENDPOINTS.csvDownload}?${queryString}`;

      const response = await fetch(url, {
        method: "GET",
        credentials: "include",
      });

      logger.log("startCSVDownloadWithEmail response:", response);

      if (!response.ok) {
        if (response.status === 402) {
          throw new Error("Insufficient credits");
        } else if (response.status === 403) {
          throw new Error("You do not have permission to perform this action. Please upgrade your plan.");
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      }

      return true;
    } catch (error) {
      console.error("Premium CSV download initialization error:", error);
      throw new Error(
        `Failed to start premium CSV download: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }
  }

  /**
   * Checks the status of a premium CSV download
   * @throws Error if the fetch request fails
   */
  static async checkCSVDownloadWithEmailStatus(
    searchId: string | number,
  ): Promise<"created" | "processing" | "completed"> {
    try {
      const statusParam = {
        search_id: searchId.toString(),
      };
      const statusQueryString = new URLSearchParams(statusParam).toString();
      const response = await fetch(`${this.BASE_URL}${this.ENDPOINTS.checkCSVStatus}?${statusQueryString}`, {
        method: "GET",
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const { status } = await response.json();
      return status;
    } catch (error) {
      throw new Error(`Failed to  premium CSV status: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }

  /**
   * Completes the premium CSV download process and downloads the file
   * @throws Error if the fetch request fails
   */
  static async completeCSVDownloadWithEmail(searchId: string | number, searchQuery?: string): Promise<void> {
    try {
      const statusParam = {
        search_id: searchId.toString(),
      };
      const queryString = new URLSearchParams(statusParam).toString();
      const response = await fetch(`${this.BASE_URL}${this.ENDPOINTS.csvDownloadWithEmail}?${queryString}`, {
        method: "GET",
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = downloadUrl;
      const filename = searchQuery
        ? `${searchQuery.toLowerCase().replace(/\s+/g, "-")}-premium-${Date.now()}.csv`
        : `search-data-pro-${searchId}.csv`;

      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Premium CSV download completion error:", error);
      throw new Error(
        `Failed to complete premium CSV download: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }
  }
  /**
   * Validates and normalizes an array field
   * @private
   */
  private static validateArray(value: unknown, fieldName: string): string[] {
    if (!value) return [];
    if (!Array.isArray(value)) {
      throw new Error(`${fieldName} must be an array`);
    }
    return value.map((item) => {
      if (typeof item !== "string") {
        throw new Error(`${fieldName} array must contain only strings`);
      }
      return item;
    });
  }

  /**
   * Validates and normalizes a string field
   * @private
   */
  private static validateString(value: unknown, fieldName: string, defaultValue = ""): string {
    if (!value) return defaultValue;
    if (typeof value !== "string") {
      throw new Error(`${fieldName} must be a string`);
    }
    return value;
  }

  /**
   * Validates and normalizes a boolean field
   * @private
   */
  private static validateBoolean(value: unknown, fieldName: string, defaultValue = false): boolean {
    if (!value) return defaultValue;
    if (typeof value !== "boolean") {
      throw new Error(`${fieldName} must be a boolean`);
    }
    return value;
  }

  static async getSearchResultsWithCursor(
    searchID: string,
    lastId: string | null,
    limit: number,
    auth?: boolean,
    userId?: string,
  ): Promise<SearchResult[]> {
    const endpoint = auth ? "/get_paginated_candidates_with_cursor_auth" : "/get_paginated_candidates_with_cursor";

    try {
      const response = await fetch(`${this.BASE_URL}${endpoint}`, {
        method: "POST",
        headers: {
          [this.HEADERS.contentType]: this.HEADERS.jsonContentType,
        },
        body: JSON.stringify({
          searchId: Number(searchID),
          lastId: lastId ? Number(lastId) : null,
          limit,
        }),
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Search results cursor fetch error:", error);
      throw new Error(`Failed to fetch search results: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }

  /**
   * Validates and normalizes a number field
   * @private
   */
  private static validateNumber(value: unknown, fieldName: string, defaultValue = 0): number {
    if (!value) return defaultValue;
    if (typeof value !== "number") {
      throw new Error(`${fieldName} must be a number`);
    }
    return value;
  }

  /**
   * Validates and normalizes a field that can be either an array or string
   * @private
   */
  private static validateArrayOrString(value: unknown, fieldName: string, defaultValue = ""): string[] | string {
    if (!value) return defaultValue;
    if (Array.isArray(value)) {
      return value.map((item) => {
        if (typeof item !== "string") {
          throw new Error(`${fieldName} array must contain only strings`);
        }
        return item;
      });
    }
    if (typeof value !== "string") {
      throw new Error(`${fieldName} must be either a string or an array`);
    }
    return value;
  }
  /**
   * Validates and normalizes the model field
   * @private
   */
  private static validateModel(value: unknown): QueryMetadataResponse["model"] {
    if (!value || typeof value !== "object") {
      return this.DEFAULT_MODEL;
    }

    const modelObj = value as Record<string, unknown>;

    if (typeof modelObj.value !== "string" || typeof modelObj.label !== "string") {
      return this.DEFAULT_MODEL;
    }

    return {
      value: modelObj.value,
      label: modelObj.label,
    };
  }

  /**
   * Creates an EventSource for search operations using search ID
   */
  static createSearchEventSource(searchId: string, userID?: string): EventSource {
    const params = new URLSearchParams({
      search_id: searchId,
      ...(userID && { user_id: userID }),
    });
    const url = this.buildUrl(this.ENDPOINTS.subscribe, params);
    return new EventSource(url, { withCredentials: true });
  }

  static createExpandSearchEventSource(searchId: string, userID?: string): EventSource {
    const params = new URLSearchParams({
      search_id: searchId,
      ...(userID && { user_id: userID }),
    });
    const url = this.buildUrl(this.ENDPOINTS.expandSubscribe, params);
    return new EventSource(url, { withCredentials: true });
  }

  /**
   * Builds search parameters for the expand search operation
   */
  static buildExpandSearchParams(data: ExpandSearchParams): URLSearchParams {
    if (!data.search_id) {
      console.error("Missing searchid for expand search");
      throw new Error("searchid is required for expand search");
    }
    return new URLSearchParams({
      search_id: data.search_id,
      definiteMatchCount: data.definiteMatchCount,
    });
  }

  /**
   * Decodes a base64 encoded search result
   * @throws Error if decoding or parsing fails
   */
  static decodeSearchResult(data: string): unknown {
    try {
      const decodedData = atob(data);
      return JSON.parse(decodedData);
    } catch (error) {
      throw new Error(`Failed to decode search result: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }

  /**
   * Joins array parameters with comma
   * @private
   */
  private static joinArrayParam(arr: string[]): string {
    return arr.join(",");
  }

  /**
   * Builds a full URL with parameters
   * @private
   */
  private static buildUrl(endpoint: string, params: URLSearchParams): string {
    return `${this.BASE_URL}${endpoint}?${params.toString()}`;
  }
}
